import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, Flag, AlertTriangle, CheckCircle, XCircle, 
  Ban, UserX, UserCheck, Eye, Trash2, MessageSquare, FileText,
  BarChart3, Clock, TrendingUp, Settings, Bell, Search,
  ChevronRight, Filter, RefreshCw, Download, Heart, Building2,
  Gavel, Scale, Crown, Diamond, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import FloatingDock from '@/components/layout/FloatingDock';
import Footer from '@/components/layout/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPanel({ user: propUser }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(propUser);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('queue');
  const [queue, setQueue] = useState(null);
  const [reports, setReports] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('session_token') || localStorage.getItem('token') || localStorage.getItem('socyy_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    // Check user from prop or try to get from stored data
    const userData = propUser || JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.is_admin) {
      toast.error('Admin access required');
      navigate('/dashboard');
      return;
    }
    setUser(userData);
    loadData();
  }, [propUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [queueRes, reportsRes, analyticsRes] = await Promise.all([
        fetch(`${API_URL}/api/moderation/queue`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/moderation/reports?status=${statusFilter}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/moderation/analytics/moderation`, { headers: getAuthHeaders() })
      ]);
      
      const [queueData, reportsData, analyticsData] = await Promise.all([
        queueRes.json(),
        reportsRes.json(),
        analyticsRes.json()
      ]);
      
      setQueue(queueData);
      setReports(reportsData.reports || []);
      setAnalytics(analyticsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleModerateReport = async (reportId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/moderation/reports/moderate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ report_id: reportId, action })
      });
      
      if (res.ok) {
        toast.success(`Report ${action === 'dismiss' ? 'dismissed' : 'actioned'}`);
        loadData();
      } else {
        toast.error('Failed to moderate report');
      }
    } catch (error) {
      toast.error('Error moderating report');
    }
  };

  const handleUserAction = async (userId, action, reason = '') => {
    try {
      const res = await fetch(`${API_URL}/api/admin/users/action`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ user_id: userId, action, reason })
      });
      
      if (res.ok) {
        toast.success(`User ${action}ed successfully`);
        loadData();
      } else {
        toast.error('Failed to perform action');
      }
    } catch (error) {
      toast.error('Error performing action');
    }
  };

  // Queue Tab
  const renderQueue = () => (
    <div className="space-y-6" data-testid="admin-queue">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-500/20 rounded-lg">
              <Flag className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{queue?.summary?.pending_reports || 0}</p>
              <p className="text-xs text-gray-400">Pending Reports</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <FileText className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{queue?.summary?.pending_verifications?.total || 0}</p>
              <p className="text-xs text-gray-400">Verifications</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <Scale className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{queue?.summary?.pending_handle_disputes || 0}</p>
              <p className="text-xs text-gray-400">Handle Disputes</p>
            </div>
          </div>
        </Card>
        
        <Card className="p-4 bg-gradient-to-br from-yellow-500/10 to-yellow-600/5 border-yellow-500/20">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{queue?.summary?.flagged_users || 0}</p>
              <p className="text-xs text-gray-400">Flagged Users</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Priority Items */}
      {queue?.priority_items?.length > 0 && (
        <Card className="p-4 border-orange-500/30 bg-orange-500/5">
          <h3 className="font-bold mb-4 flex items-center gap-2 text-orange-400">
            <AlertTriangle className="w-5 h-5" />
            Priority Items
          </h3>
          <div className="space-y-3">
            {queue.priority_items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center gap-3">
                  {item.type === 'multiple_reports' && <Flag className="w-4 h-4 text-red-400" />}
                  {item.type === 'streamlined_verification' && <CheckCircle className="w-4 h-4 text-green-400" />}
                  <div>
                    <p className="font-medium">{item.type.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-gray-400">
                      {item.user?.username && `@${item.user.username}`}
                      {item.report_count && ` • ${item.report_count} reports`}
                    </p>
                  </div>
                </div>
                <Button size="sm" variant="outline" className="text-xs">
                  Review <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Verification Breakdown */}
      <Card className="p-4">
        <h3 className="font-bold mb-4">Pending Verifications</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-gray-900/50 rounded-lg text-center">
            <Users className="w-6 h-6 mx-auto mb-2 text-blue-400" />
            <p className="text-xl font-bold">{queue?.summary?.pending_verifications?.identity || 0}</p>
            <p className="text-xs text-gray-400">Identity</p>
          </div>
          <div className="p-3 bg-gray-900/50 rounded-lg text-center">
            <Building2 className="w-6 h-6 mx-auto mb-2 text-green-400" />
            <p className="text-xl font-bold">{queue?.summary?.pending_verifications?.business || 0}</p>
            <p className="text-xs text-gray-400">Business</p>
          </div>
          <div className="p-3 bg-gray-900/50 rounded-lg text-center">
            <Heart className="w-6 h-6 mx-auto mb-2 text-pink-400" />
            <p className="text-xl font-bold">{queue?.summary?.pending_verifications?.nonprofit || 0}</p>
            <p className="text-xs text-gray-400">Non-Profit</p>
          </div>
        </div>
      </Card>
    </div>
  );

  // Reports Tab
  const renderReports = () => (
    <div className="space-y-4" data-testid="admin-reports">
      <div className="flex items-center gap-4 mb-4">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            loadData();
          }}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm"
        >
          <option value="pending">Pending</option>
          <option value="under_review">Under Review</option>
          <option value="resolved">Resolved</option>
          <option value="all">All</option>
        </select>
        <Button variant="outline" size="sm" onClick={loadData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {reports.length === 0 ? (
        <Card className="p-8 text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
          <p className="text-gray-400">No {statusFilter} reports</p>
        </Card>
      ) : (
        reports.map(report => (
          <Card key={report.report_id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    report.content_type === 'user' ? 'bg-red-500/20 text-red-400' :
                    report.content_type === 'post' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {report.content_type}
                  </span>
                  <span className="text-sm text-gray-400">{report.reason}</span>
                </div>
                
                <p className="text-sm mb-2">{report.details || 'No additional details'}</p>
                
                {report.content_info?.text && (
                  <div className="p-2 bg-gray-900/50 rounded text-sm text-gray-400 mb-2">
                    "{report.content_info.text}"
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span>Reported by: @{report.reporter?.username}</span>
                  <span>Against: @{report.reported_user?.username}</span>
                  <span>{new Date(report.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              
              {report.status === 'pending' && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-400 border-green-500/30"
                    onClick={() => handleModerateReport(report.report_id, 'dismiss')}
                  >
                    <XCircle className="w-4 h-4 mr-1" /> Dismiss
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-yellow-400 border-yellow-500/30"
                    onClick={() => handleModerateReport(report.report_id, 'warn')}
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" /> Warn
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-500"
                    onClick={() => handleModerateReport(report.report_id, 'suspend_user')}
                  >
                    <Ban className="w-4 h-4 mr-1" /> Suspend
                  </Button>
                </div>
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  );

  // Analytics Tab
  const renderAnalytics = () => (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <h4 className="text-sm text-gray-400 mb-2">Verification Approval Rate</h4>
          <p className="text-3xl font-bold text-green-400">{analytics?.verifications?.approval_rate || 0}%</p>
          <p className="text-xs text-gray-500">
            {analytics?.verifications?.approved || 0} approved / {analytics?.verifications?.rejected || 0} rejected
          </p>
        </Card>
        
        <Card className="p-4">
          <h4 className="text-sm text-gray-400 mb-2">Reports by Reason</h4>
          <div className="space-y-2">
            {analytics?.reports_by_reason?.slice(0, 3).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-400">{item._id}</span>
                <span className="font-bold">{item.count}</span>
              </div>
            ))}
          </div>
        </Card>
        
        <Card className="p-4">
          <h4 className="text-sm text-gray-400 mb-2">Actions Taken (30d)</h4>
          <div className="space-y-2">
            {analytics?.actions_taken?.slice(0, 3).map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-gray-400">{item._id}</span>
                <span className="font-bold">{item.count}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-black to-gray-950 text-white" data-testid="admin-panel">
      <FloatingDock user={user} />
      
      <main className="max-w-6xl mx-auto px-4 py-8 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl">
              <Shield className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Admin Panel</h1>
              <p className="text-gray-400 text-sm">Moderation & Management</p>
            </div>
          </div>
          <Button
            onClick={() => navigate('/admin/dashboard')}
            className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
            data-testid="monitoring-dashboard-btn"
          >
            <Activity className="w-4 h-4" />
            Monitoring Dashboard
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-gray-900/50 p-1 rounded-xl w-fit">
          {[
            { id: 'queue', label: 'Queue', icon: Clock },
            { id: 'reports', label: 'Reports', icon: Flag },
            { id: 'verifications', label: 'Verifications', icon: FileText },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                activeTab === tab.id
                  ? 'bg-red-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'queue' && renderQueue()}
        {activeTab === 'reports' && renderReports()}
        {activeTab === 'analytics' && renderAnalytics()}
        {activeTab === 'verifications' && (
          <Card className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-blue-400" />
            <p className="text-gray-400">Verification management coming soon</p>
          </Card>
        )}
      </main>
    </div>
  );
}
